/**
 * Helpers: DOM
 * ------------------------------------------------------------------------------
 * - Collection of helper functions related to the DOM.
 * - Add new functions in alphabetical order.
 *
 */
import { storageNamespace } from '~/helpers/storage-namespaces'

/**
 * Detect if the the User Agent is that of a search bot.
 * - https://stackoverflow.com/a/65563155
 * - https://developers.google.com/search/docs/advanced/crawling/overview-google-crawlers
 * @returns {Boolean}
 */
export function isRobot() {
  const robots = new RegExp([
    /AdsBot/,
    /adsbot-google/,
    /APIs-Google/,
    /baidu/,
    /bing/,
    /bot/,
    /crawl/,
    /duckduck/,
    /DuplexWeb-Google/,
    /ecosia/,
    /facebook/,
    /FeedFetcher/,
    /Google Favicon/,
    /Google-InspectionTool/,
    /Google-Read-Aloud/,
    /Googlebot/,
    /googleweblight/,
    /ia_archiver/,
    /instagram/,
    /mediapartners/,
    /pinterest/,
    /reddit/,
    /Screaming Frog/,
    /semrush/,
    /slack/,
    /spider/,
    /Storebot-Google/,
    /twitter/,
    /whatsapp/,
    /yahoo/,
    /yandex/,
    /youtube/,
  ].map((robot) => robot.source).join('|'), 'i')

  return robots.test(navigator.userAgent)
}

/**
 * Dynamically insert a stylesheet into the DOM.
 * @param {String} src - full URL src of the sheet to be inserted.
 * @param {HTMLElement} target - Where to insert the stylesheet.
 */
export function insertStylesheet({ src, target = document.head }) {
  const existingStyle = [...Object.values(document.styleSheets)].find((stylesheet) => {
    if (!stylesheet.href) {
      return false
    }

    const cleanSrc = src.split('?v=')[0]

    return stylesheet.href.includes(cleanSrc)
  })

  if (existingStyle) {
    return
  }

  const newSheet = document.createElement('link')
  newSheet.setAttribute('rel', 'stylesheet')
  newSheet.setAttribute('type', 'text/css')
  newSheet.href = src

  target.appendChild(newSheet)
}

/**
 * Detects if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @returns {Boolean} - True if element is at least partially in viewport.
 */
export function isVisible(element) {
  const windowHeight = window.innerHeight

  const elementRectangle = element.getBoundingClientRect()
  const elementTop = elementRectangle.top
  const elementBottom = elementRectangle.bottom

  const topInView = (elementTop > 0) && (elementTop < windowHeight)
  const bottomInView = (elementBottom > 0) && (elementBottom < windowHeight)

  return (topInView || bottomInView)
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Object} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - This set of values serves to grow or shrink
 * each side of the root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {Function}
 */
export function setIntersectionObserver({
  callback,
  element,
  rootMargin = '0px',
  threshold = 0.5,
}) {
  const options = {
    root: null,
    rootMargin,
    threshold,
  }

  const observer = new IntersectionObserver(callback, options)
  observer.observe(element)
}

/**
 * Wait for a window variable to be defined before proceeding with function.
 * - Supports top-level variables (window.Shopify)
 * - Supports nested object variables (window.Shopify.theme)
 * @example
 * waitFor('Shopify')
 * .then(() => {
 *   // Waits until `window.Shopify` is defined before proceeding.
 * })
 * @param {String} namespace - Named window variable to wait for.
 * @return {Object}
 */
export function waitFor(namespace) {
  return new Promise((resolve, reject) => {

    /**
     * Wait for a maximum of 15 seconds at 0.5s intervals
     */
    const timeout = 15000
    let count = 0
    const loop = 500
    const maxSeconds = timeout / (loop * 2)

    const interval = setInterval(() => {
      const target = namespace.split('.').reduce((obj, key) => {
        if (!obj[key]) {
          return false
        }

        return obj[key]
      }, window)

      if (!target) {
        if (count > maxSeconds) {
          mint.Log('warning', 'Global variable timed out', new Error('Global variable timed out'), {
            namespace,
          })

          clearInterval(interval)
          reject(false)
          return
        }

        count++
        return
      }

      clearInterval(interval)
      resolve(true)
    }, loop)
  })
}

/**
 * Waits for a condition to be true and returns a promise.
 * - After a timeout, will return false.
 * - Pass in a function that is expected to return something to check.
 * @example:
 *  await waitForCondition(_ => Number(1) > Number(2))
 *  await waitForCondition(_ => Boolean(5 > 1))
 *  await waitForCondition(_ => myFunction())
 * @param {Function} conditionFunction
 * @returns {Promise}
 */
export function waitForCondition(condition) {
  return new Promise((resolve, reject) => {

    /**
     * Wait for a maximum of 4 seconds at 0.25s intervals
     */
    const timeout = 4000
    let count = 0
    const loop = 250
    const maxSeconds = timeout / (loop * 4)

    const interval = setInterval(() => {
      if (!condition()) {
        if (count > maxSeconds) {
          mint.Log('warning', 'waitForCondition timed out',
            new Error('waitForCondition timed out'), {
              condition,
            })

          clearInterval(interval)
          reject(false)
        }

        count++
        return
      }

      clearInterval(interval)
      resolve(true)
    }, loop)
  })
}

/**
 * A function that waits for the appearance of a certain html element in the DOM
 * @param {String} selector - css selector for element which must be monitored
 * @returns {Promise} - A promise that resolves to the first element
 * that matches the selector.
 */
export function waitForElem(selector) {
  return new Promise(resolve => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector))
    }
    const observer = new MutationObserver(mutations => {
      if (document.querySelector(selector)) {
        observer.disconnect()
        resolve(document.querySelector(selector))
      }
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
  })
}

/**
 * For PLPs, ensure that when the user goes 'Back', they are
 * restored to their previous position.
 * - This exists as we're unable to use `bfcache` due to an `unload`
 *   event that Bloomreach and jQuery uses.
 * - When a user clicks on a PLP product card, sessionStorage is set with their position
 * - PLP will then check for this and adjust the Attraqt query, scrolling the user back.
 * - Using these methods appear to be more reliable than detecting browser-based 'Back' events.
 * - https://mint-velvet.atlassian.net/wiki/spaces/TECHNOLOGY/pages/105119745/Restore+Scroll+Position+on+browser+Back
 */
export function setCollectionScrollRestoreState() {
  const cache = JSON.parse(
    sessionStorage.getItem(storageNamespace.plp.scrollRestore),
  ) || {}

  if (!Object.keys(cache).length) {
    return
  }

  const plpPageTypes = [
    'collection',
    'search',
  ]

  /**
   * Set default to true to decrease chances of incorrectly scrolling the user.
   */
  let deleteStorage = true

  /**
   * The user is on the same PLP where they initially clicked a product card.
   */
  if (
    plpPageTypes.includes(mint.request.pageType) &&
    cache.type === 'collection' &&
    mint.collection.handle === cache.query
  ) {
    deleteStorage = false
  }

  /**
   * The user is on the same 'Search' page where they previously clicked a product card.
   */
  if (
    plpPageTypes.includes(mint.request.pageType) &&
    cache.type === 'search' &&
    location.search.includes(cache.query)
  ) {
    deleteStorage = false
  }

  /**
   * The user is on the PDP that they clicked on via the PLP.
   */
  if (
    mint.request.pageType === 'product' &&
    mint.request.path.includes(cache.product)
  ) {
    deleteStorage = false
  }

  if (!deleteStorage) {
    return
  }

  sessionStorage.removeItem(storageNamespace.plp.scrollRestore)
}

/**
 * Parse and format the attribute value based on its type.
 * @param {string} value - The attribute value to be parsed.
 * @returns {*} - The parsed and formatted attribute value.
 */
export function parseAttributeValue(value) {
  try {
    // Attempt to parse the value as JSON
    const parsedValue = JSON.parse(value)

    // If not a boolean or JSON, handle it as a string
    return typeof parsedValue === 'object' || typeof parsedValue === 'boolean'
      ? parsedValue
      : value
  } catch (error) {
    // If JSON parse fails, handle it as a string
    return value
  }
}
